import classNames from 'classnames'
import React, {
  ChangeEventHandler,
  forwardRef,
  KeyboardEventHandler,
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import ChevronIcon from '../../assets/icons/chevron-small.svg'
import { useDeviceDetect } from '../../hooks/useDeviceDetect'
import { useTranslations } from '../../hooks/useTranslations'
import { useWindowScroll } from '../../hooks/useWindowScroll'
import {
  isAllEqualCharacters,
  isNullOrWhiteSpace,
  stripNonAlphaNumeric,
} from '../../utilities/string'
import { DropdownItem } from './DropdownItem'
import { Label } from './Label'

export interface DropdownOption {
  label: string
  value: string
}

interface OptionItem {
  id: string
  isPlaceholder: boolean
  label: string
  value: string
}

export type OnRenderOptionElementProps = {
  'aria-selected': boolean
  id: string
  onClick: () => void
  role: 'option'
}

export type OnRenderOptionAdditionalProps = {
  focused: boolean
  label: string
  selected: boolean
}

export type OnRenderOptionCallback = (
  value: string,
  elementProps: OnRenderOptionElementProps,
  additionalProps: OnRenderOptionAdditionalProps
) => JSX.Element

export type OnRenderValueCallback = (value: string, label: string) => JSX.Element

export type RoundedBorders = {
  bottomLeft?: boolean
  bottomRight?: boolean
  topLeft?: boolean
  topRight?: boolean
}

export type DropdownProps = {
  additionalClassName?: string
  applyRelative?: boolean
  disabled?: boolean
  displayValidationMessage?: boolean
  flyoutMaxWidth?: string
  id?: string
  label?: string
  name: string
  onBlur?: () => void
  onChange?: (value: string) => void
  onRenderOption?: OnRenderOptionCallback
  onRenderValue?: OnRenderValueCallback
  options: DropdownOption[]
  placeholder?: string
  required?: boolean
  roundedBorders?: RoundedBorders
  searchField?: 'label' | 'value'
  showInMobile?: boolean
  theme?: 'light' | 'dark'
  useNativeSelectInMobile?: boolean
  validation?: true | string
  value: string | undefined
  variant?: 'inline' | 'button'
}

/**
 * Keeps native select control in mobile devices.
 * Uses a custom control in a normal browser.
 * Uses the hidden native select control for posting values in forms
 * instead of using hidden controls.
 *
 * NOTE: before making a multiselect version, consider that some
 * mobile browsers (e.g. FF Mobile) show a list control for multiselect.
 *
 * Based on https://w3c.github.io/aria-practices/examples/combobox/combobox-select-only.html
 * and https://www.w3.org/TR/wai-aria-practices/examples/listbox/listbox-collapsible.html
 */
export const Dropdown = forwardRef<HTMLSelectElement, DropdownProps>(
  (
    {
      additionalClassName,
      applyRelative = true,
      disabled,
      displayValidationMessage = true,
      flyoutMaxWidth = 'max-w-[max(100%,20.625rem)]',
      id,
      label,
      name,
      onBlur,
      onChange: onChangeProps,
      onRenderOption,
      onRenderValue,
      options: rawOptions,
      placeholder = '',
      required,
      roundedBorders,
      searchField = 'label',
      showInMobile = true,
      theme = 'dark',
      useNativeSelectInMobile = true,
      validation,
      value = '',
      variant = 'inline',
    },
    ref
  ) => {
    const PAGE_SIZE = 10
    const MAX_HEIGHT = 200

    const [expanded, setExpanded] = useState(false)
    const [directionUp, setDirectionUp] = useState(false)
    const [overflowing, setOverflowing] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [focusedIndex, setFocusedIndex] = useState(0)
    const [isKeyboardUser, setIsKeyboardUser] = useState(false)

    const expandPosition = useRef(0)
    const isExpanding = useRef(true)
    const buttonRef = useRef<HTMLButtonElement>(null)
    const popupRef = useRef<HTMLUListElement>(null)
    const searchString = useRef('')
    const searchTimer = useRef<NodeJS.Timeout>()

    const translations = useTranslations()
    const deviceDetect = useDeviceDetect()
    const scrollYPosition = useWindowScroll()

    // all options, including placeholder
    const optionItems: OptionItem[] = useMemo(() => {
      const prefix = id ? `${id}-opt-${name}` : `opt-${name}`
      const optionItems = rawOptions.map((opt, index) => {
        const val = stripNonAlphaNumeric(opt.value)
        const id = `${prefix}-${val}-${index}`
        return {
          ...opt,
          id,
          isPlaceholder: false,
        }
      })

      if (!isNullOrWhiteSpace(placeholder)) {
        optionItems.unshift({
          id: `${prefix}-none`,
          isPlaceholder: true,
          label: placeholder,
          value: '',
        })
      }

      return optionItems
    }, [name, rawOptions, placeholder])

    const setDirectionAndScroll = useCallback(() => {
      // expand up if there's no room below
      const btnRect = buttonRef.current?.getBoundingClientRect()
      if (btnRect) {
        const spaceAbove = btnRect.top
        const spaceBelow = window.innerHeight - btnRect.bottom
        setDirectionUp(spaceAbove > spaceBelow && spaceBelow < MAX_HEIGHT + 16)
      }

      const popup = popupRef.current
      if (popup) {
        // check if the popup sticks out to the right of the screen
        popup.style.right = 'auto'
        if (popup.getBoundingClientRect().right > window.innerWidth - 16) {
          setOverflowing(true)
        } else {
          setOverflowing(false)
        }
        popup.style.right = ''

        // reset scroll to avoid scrolling to the bottom of the first items if the
        // popup is scrolled down without making a selection.
        popup.scrollTop = 0
      }

      expandPosition.current = scrollYPosition
    }, [scrollYPosition])

    const onClose = useCallback((focusButton = true) => {
      setExpanded(false)

      if (focusButton) {
        buttonRef.current?.focus()
      }
    }, [])

    const onOpen = useCallback(() => {
      const isExpanded = expanded
      if (!isExpanded) {
        isExpanding.current = true
        setExpanded(true)
        setDirectionAndScroll()
        setTimeout(() => (isExpanding.current = false), 1000)
      }
    }, [expanded, setDirectionAndScroll])

    const onSearch = useCallback(
      (key: string, repeatedKey = false) => {
        // reset timeout
        if (searchTimer?.current) {
          clearTimeout(searchTimer.current)
        }

        searchTimer.current = setTimeout(() => (searchString.current = ''), 500)

        searchString.current = repeatedKey
          ? key.toUpperCase()
          : searchString.current + key.toUpperCase()

        const current = searchString.current
        const items = optionItems.map(o => {
          const searchString = searchField === 'value' ? o.value : o.label
          return searchString.substring(0, current.length).toUpperCase()
        })
        const fromIndex = current.length === 1 ? focusedIndex + 1 : focusedIndex
        let index = items.indexOf(current, fromIndex)
        if (index === -1) {
          // wrap around to start
          index = items.indexOf(current)
        }

        if (index >= 0) {
          setFocusedIndex(index)
        } else if (current.length > 1 && isAllEqualCharacters(current)) {
          // repeated character, skip forward and search by first character
          onSearch(key, true)
        }
      },
      [focusedIndex, optionItems, searchField]
    )

    const onSelect = useCallback(
      (index: number, focusButton = true) => {
        if (index !== selectedIndex) {
          setSelectedIndex(index)

          const _value = optionItems[index]?.value ?? ''

          onChangeProps?.(_value)
        }

        onClose(focusButton)
      },
      [onChangeProps, onClose, optionItems, selectedIndex]
    )

    const onChange = useCallback<ChangeEventHandler<HTMLSelectElement>>(
      e => {
        onSelect(e.target.selectedIndex)
      },
      [onSelect]
    )

    const onClickOption = useCallback(
      (index: number) => {
        setIsKeyboardUser(false)
        onSelect(index)
      },
      [onSelect]
    )

    const onClickButton = useCallback<MouseEventHandler>(() => {
      setIsKeyboardUser(false)
      // reset focus when switching to mouse
      setFocusedIndex(selectedIndex)
      if (expanded) {
        // prevent default on mouse down stops the blur event from firing before the click event
        onClose()
      } else {
        onOpen()
      }
    }, [expanded, onClose, onOpen, selectedIndex])

    const onKeypressButton = useCallback<KeyboardEventHandler>(
      e => {
        setIsKeyboardUser(true)
        const { key, altKey, ctrlKey, metaKey } = e
        const openKeys = ['ArrowDown', 'ArrowUp', 'Enter', ' ', 'Home', 'End']
        const homeKeys = ['ArrowUp', 'Home']
        const isSearch =
          key.length === 1 && key !== ' ' && !altKey && !ctrlKey && !metaKey

        // handle opening when closed
        if (openKeys.includes(key) || isSearch) {
          if (homeKeys.includes(key)) {
            setFocusedIndex(0)
          } else if (key === 'End') {
            setFocusedIndex(optionItems.length - 1)
          }

          e.preventDefault()
          onOpen()
          if (isSearch) {
            e.stopPropagation()
            onSearch(key)
          }
        }
      },
      [onOpen, onSearch, optionItems.length]
    )

    const onKeypressPopup = useCallback<KeyboardEventHandler>(
      e => {
        setIsKeyboardUser(true)
        const { key, altKey, ctrlKey, metaKey } = e
        const selectKeys = ['Tab', 'Enter', ' ']
        const navKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End']

        if (key === 'Escape') {
          e.preventDefault()
          onClose()
        } else if (selectKeys.includes(key) || (key === 'ArrowUp' && altKey)) {
          if (key === 'Tab') {
            onSelect(focusedIndex, false)
          } else {
            e.preventDefault()
            onSelect(focusedIndex)
          }
        } else if (navKeys.includes(key)) {
          let index = focusedIndex
          const max = optionItems.length - 1

          switch (key) {
            case 'ArrowUp':
              index = Math.max(0, index - 1)
              break
            case 'PageUp':
              index = Math.max(0, index - PAGE_SIZE)
              break
            case 'ArrowDown':
              index = Math.min(max, index + 1)
              break
            case 'PageDown':
              index = Math.min(max, index + PAGE_SIZE)
              break
            case 'Home':
              index = 0
              break
            case 'End':
              index = max
              break
          }

          e.preventDefault()
          setFocusedIndex(index)
        }

        if (key.length === 1 && key !== ' ' && !altKey && !ctrlKey && !metaKey) {
          e.preventDefault()
          e.stopPropagation()
          onSearch(key)
        } else {
          searchString.current = ''
        }
      },
      [focusedIndex, onClose, onSearch, onSelect, optionItems.length]
    )

    useEffect(() => {
      // delayed due to the control scrolling into view when receiving focus (with smooth scroll, which takes some time)
      if (!isExpanding.current && expandPosition.current !== undefined) {
        // add some wiggle room for touch screen users
        const distance = Math.abs(scrollYPosition - expandPosition.current)

        if (distance > 50) {
          setExpanded(false)
        }
      }
    }, [scrollYPosition])

    // Cleanup timer on unmount
    useEffect(
      () => () => {
        if (searchTimer?.current) {
          clearTimeout(searchTimer.current)
        }
      },
      []
    )

    useEffect(() => {
      const index = optionItems.findIndex(opt => opt.value === value)
      setSelectedIndex(index)
      setFocusedIndex(index)
    }, [optionItems, value])

    useEffect(() => {
      if (expanded) {
        popupRef.current?.focus()

        const focusedItemId = optionItems[focusedIndex]?.id

        document?.getElementById(focusedItemId)?.scrollIntoView({
          block: 'nearest', // to avoid causing scroll in the page
          inline: 'nearest', // avoid horizontal scroll in chrome
        })
      }
    }, [expanded, focusedIndex, optionItems])

    // Use the native control if the user is on a mobile device or tablet.
    // Note: cannot be determined by touch/coarse pointer or viewport alone,
    // since that doesn't mean that the browser will replace the select control
    // (e.g. windows tablet or touch laptop)
    const useCustomSelect =
      !useNativeSelectInMobile || (!deviceDetect.isMobile && !deviceDetect.isTablet)

    let selectClassName = classNames(
      'w-full pb-15 pr-20 border-b cursor-pointer appearance-none outline-none focus:outline-none duration-200',
      'font-t text-t-300',

      {
        'pb-15 pr-20 bg-transparent border-b rounded-none xl:text-t-400 xxl:text-t-600':
          variant === 'inline',
        'text-neutral': variant === 'inline' && theme === 'light',
        'text-secondary-darker': variant === 'inline' && theme === 'dark',
      },
      {
        'pl-15 pr-30 py-15 bg-neutral border focus:ring': variant === 'button',
        'text-neutral-dark-alt': variant === 'button' && !value,
        'text-secondary-darker': variant === 'button' && value,
      }
    )

    if (variant === 'button') {
      selectClassName = classNames(selectClassName, {
        'rounded-15': !roundedBorders,
        'rounded-bl-15': roundedBorders?.bottomLeft,
        'rounded-br-15': roundedBorders?.bottomRight,
        'rounded-tl-15': roundedBorders?.topLeft,
        'rounded-tr-15': roundedBorders?.topRight,
      })

      if (validation === true && value) {
        selectClassName = classNames(
          selectClassName,
          'border-success focus:ring-success/40'
        )
      } else if (validation !== true && !isNullOrWhiteSpace(validation)) {
        selectClassName = classNames(
          selectClassName,
          'border-secondary focus:ring-error/40'
        )
      } else {
        selectClassName = classNames(selectClassName, 'focus:ring-primary/40', {
          'border-secondary-darker': value,
          'border-neutral-lighter-alt hover:border-secondary-darker focus:border-secondary-darker':
            !value,
        })
      }
    } else {
      selectClassName = classNames(
        selectClassName,
        'hover:border-primary focus:border-primary-alpha-25',
        {
          'border-neutral': theme === 'light' && !value,
          'border-neutral-light': theme !== 'light' && !value,
        }
      )
    }

    const selectedOptionItem = optionItems[selectedIndex]

    return (
      <div
        className={classNames(
          { 'hidden tb:flex tb:flex-col': !showInMobile, 'flex flex-col': showInMobile },
          additionalClassName
        )}
      >
        <div
          className={classNames('flex flex-col flex-grow', { 'relative': applyRelative })}
        >
          {!isNullOrWhiteSpace(label) && (
            <Label additionalClassName="mb-10" htmlFor={name} required={required}>
              {label}
            </Label>
          )}

          <div
            className={classNames('flex relative flex-grow', {
              'hidden': useCustomSelect,
            })}
          >
            <select
              className={selectClassName}
              disabled={disabled}
              id={id ?? name}
              name={name}
              onChange={onChange}
              ref={ref}
              value={value}
              required={required}
            >
              {optionItems?.map((option, index) => (
                <option
                  className={
                    option.isPlaceholder ? 'text-neutral-dark' : 'text-neutral-contrast'
                  }
                  key={index}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </select>

            <ChevronIcon
              className={classNames(
                'absolute self-center right-0 transition-all duration-200 pointer-events-none',
                {
                  'mb-5 fill-primary w-[12px] h-[12px]': variant === 'inline',
                  'mr-15 fill-secondary-darker w-[10px] h-[10px]': variant === 'button',
                  '-rotate-90': !expanded,
                  'rotate-90': expanded,
                }
              )}
            />
          </div>

          {useCustomSelect && (
            <>
              <div className="flex relative flex-grow text-secondary-darker">
                <button
                  aria-expanded={expanded}
                  aria-haspopup="listbox"
                  aria-label={`${translations.selectFilters} ${
                    selectedOptionItem?.label ?? ''
                  }`}
                  className={classNames(
                    selectClassName,
                    'whitespace-nowrap overflow-x-hidden text-ellipsis text-left select-none'
                  )}
                  id={id ? `${id}-button` : undefined}
                  // Trigger RHF validation on blur when tabbing past the select
                  onBlur={onBlur}
                  onClick={onClickButton}
                  onKeyDown={onKeypressButton}
                  onMouseDown={e => e.preventDefault()} // avoid conflict with blur
                  ref={buttonRef}
                  type="button" // no submit
                >
                  {selectedOptionItem &&
                    (onRenderValue ? (
                      onRenderValue(selectedOptionItem.value, selectedOptionItem.label)
                    ) : (
                      <>{selectedOptionItem.label ?? ''}&nbsp;</>
                    ))}
                </button>

                <ChevronIcon
                  className={classNames(
                    'absolute self-center right-0 transition-all duration-200 pointer-events-none',
                    {
                      'mb-5 fill-primary w-[12px] h-[12px]': variant === 'inline',
                      'mr-15 fill-secondary-darker w-[10px] h-[10px]':
                        variant === 'button',
                      '-rotate-90': !expanded,
                      'rotate-90': expanded,
                    }
                  )}
                />
              </div>

              <ul
                tabIndex={-1}
                onBlur={() => {
                  onClose(false)

                  // Trigger RHF validation on blur
                  onBlur?.()
                }}
                onKeyDown={onKeypressPopup}
                role="listbox"
                aria-activedescendant={
                  optionItems[isKeyboardUser ? focusedIndex : selectedIndex]?.id
                }
                aria-hidden={!expanded}
                aria-label={`${translations.selectFilters}`}
                className={classNames(
                  'bg-neutral whitespace-nowrap z-navbar-search-bar font-t-bold text-t-100 text-secondary-darkest',
                  'absolute max-h-240 overflow-y-auto overscroll-contain shadow-select rounded-4 scrollbar-thin',
                  flyoutMaxWidth,
                  'focus:outline-none focus-visible:outline-none',
                  {
                    'invisible h-0': !expanded,
                    'bottom-full': directionUp,
                    'top-full': !directionUp,
                    'right-0': overflowing,
                  }
                )}
                ref={popupRef}
              >
                {optionItems.map((opt, index) =>
                  !!onRenderOption ? (
                    <React.Fragment key={index}>
                      {onRenderOption(
                        opt.value,
                        {
                          'aria-selected': selectedIndex === index,
                          id: opt.id,
                          onClick: () => onClickOption(index),
                          role: 'option',
                        },
                        {
                          focused: isKeyboardUser && focusedIndex === index,
                          label: opt.label,
                          selected: selectedIndex === index,
                        }
                      )}
                    </React.Fragment>
                  ) : (
                    <DropdownItem
                      focused={isKeyboardUser && focusedIndex === index}
                      id={opt.id}
                      index={index}
                      isPlaceholder={opt.isPlaceholder}
                      key={index}
                      label={opt.label}
                      onClick={onClickOption}
                      selected={selectedIndex === index}
                    />
                  )
                )}
              </ul>
            </>
          )}
        </div>

        {displayValidationMessage && (
          <div className="min-h-15 mt-5 text-secondary font-t text-t-200 leading-[15px]">
            {validation !== true && !isNullOrWhiteSpace(validation) && validation}
          </div>
        )}
      </div>
    )
  }
)
